import React, { useMemo } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FieldInput } from '../../../../ui/FieldInput';
import { Tabs } from '../../../../ui/Tabs';
import { ProgramCombobox } from '../../../../ui/ProgramCombobox';
import { DatePickerField } from '../../../../ui/DatePickerField';
import { Student } from './StudentListItem';
import type { Program, ProgramAssignment } from '../ProgramsManager';

const parseDate = (value?: string) => {
  if (!value) return null;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

type ProgramDateField = 'theoretical_start_date' | 'theoretical_end_date' | 'exam_date';

export interface StudentViewCardProps {
  student: Student;
  onClose: () => void;
  programs?: ProgramAssignment[];
  availablePrograms?: Program[];
  onProgramAdd?: (programId: string) => void;
  onProgramRemove?: (programId: string) => void;
  onProgramDateChange?: (programId: string, field: ProgramDateField, date: Date | null) => void;
  onStudentChange?: (studentId: string, field: 'full_name' | 'email', value: string) => void;
  errors?: { email?: string };
}

export const StudentViewCard: React.FC<StudentViewCardProps> = ({
  student,
  onClose,
  programs = [],
  availablePrograms = [],
  onProgramAdd,
  onProgramRemove,
  onProgramDateChange,
  onStudentChange,
  errors = {},
}) => {
  const availableOptions = useMemo(() => {
    const assignedIds = new Set(programs.map((p) => String(p.program.id)));
    return availablePrograms
      .filter((program) => !assignedIds.has(String(program.id)))
      .map((program) => ({ id: String(program.id), name: program.name }));
  }, [availablePrograms, programs]);

  const tabs = [
    {
      id: 'main',
      label: 'Основная информация',
      content: (
        <div className="grid grid-cols-1 gap-y-5">
          <FieldInput
            label="ФИО"
            value={student.full_name}
            placeholder="Не указан"
            onChange={(v) => onStudentChange?.(student.id, 'full_name', v)}
          />
          <div>
            <FieldInput
              label="Email"
              value={student.email || ""}
              placeholder="Не указан"
              onChange={(v) => onStudentChange?.(student.id, 'email', v)}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'programs',
      label: 'Запись на программы',
      content: (
        <div className="space-y-5">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Добавить программы</h3>
            </div>
            <ProgramCombobox
              options={availableOptions}
              onSelect={(programId: string) => onProgramAdd?.(programId)}
              placeholder="Начните вводить название программы..."
              disabled={availableOptions.length === 0}
              keepOpenOnSelect={false}
            />
            {availableOptions.length === 0 && (
              <p className="text-sm text-gray-500">Все доступные программы уже добавлены этому студенту.</p>
            )}
          </div>

          {programs.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-200 p-6 text-center text-gray-600">
              Нет выбранных программ
            </div>
          ) : (
            <div className="space-y-4">
            {programs.map((assignment) => {
              const { program } = assignment;
              const theoryStart = parseDate(assignment.theoretical_start_date);
              const theoryEnd = parseDate(assignment.theoretical_end_date);
              const examDate = parseDate(assignment.exam_date);

              return (
                <div key={program.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-base font-semibold text-gray-900">{program.name}</p>
                      <p className="text-sm text-gray-600">{program.durationHours ?? '—'} ч.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onProgramRemove?.(program.id)}
                      className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-100"
                    >
                      <DeleteOutlineIcon fontSize="small" /> Удалить
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <DatePickerField
                      label="Дата начала теории"
                      selected={theoryStart}
                      onChange={(date: Date | null) => onProgramDateChange?.(program.id, 'theoretical_start_date', date)}
                    />
                    <DatePickerField
                      label="Дата окончания теории"
                      selected={theoryEnd}
                      onChange={(date: Date | null) => onProgramDateChange?.(program.id, 'theoretical_end_date', date)}
                    />
                  </div>

                  <div className="mt-4 max-w-sm">
                    <DatePickerField
                      label="Дата экзамена"
                      selected={examDate}
                      onChange={(date: Date | null) => onProgramDateChange?.(program.id, 'exam_date', date)}
                    />
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-lg">
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none"
          >
            <ArrowBackIcon />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Информация о студенте</h2>
        </div>
      </div>
      <div className="p-4 sm:p-6">
        <Tabs items={tabs} />
      </div>
    </div>
  );
};